import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import { useHasPermission } from '~/components/hooks/useHasPermission';
import useOrganization from '~/components/OrganizationContext';

// Mirrors server/modules/gbg/schemas/validation_status.py, which the operational details serialize
// untyped; keep the two in step.
export interface GbgValidationStatusType {
  is_address_validation_enabled: boolean;
  is_business_validation_enabled: boolean;
}

const DISABLED_STATUS: GbgValidationStatusType = {
  is_address_validation_enabled: false,
  is_business_validation_enabled: false,
};

export const GBG_SUPPORTED_COUNTRIES = ['AU', 'NZ'] as const;

export const isGbgSupportedCountry = (countryCode: string | null | undefined): boolean => {
  const uppercasedCountry = countryCode?.toUpperCase() || '';
  return GBG_SUPPORTED_COUNTRIES.some((supportedCountry) => supportedCountry === uppercasedCountry);
};

/**
 * The organization's GBG validation status (address and business), from its operational details.
 * Reports GBG off to a user who cannot read contacts, since every GBG lookup route requires it.
 */
export const useGbgValidationStatus = (): GbgValidationStatusType => {
  // OrganizationContext is still JS (`createContext({})`), so the hook infers `{}`; asserting the
  // member's type gives the status a type here, which suppressing the line would not.
  const { gbgValidationStatus } = useOrganization() as { gbgValidationStatus?: GbgValidationStatusType };
  const canReadContacts = useHasPermission({ action: PERMISSION_ACTIONS.CONTACT, verb: PERMISSION_VERBS.READ });

  if (!canReadContacts || !gbgValidationStatus) return DISABLED_STATUS;

  return gbgValidationStatus;
};
